import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  IMPORT_TEMPLATES,
  type ImportTemplateId,
  type IssueGroup,
  type MappingProblem,
  type QuarantineItem,
} from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { settledWrite } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { importNotCommittableWord } from '../status-labels.js'

/**
 * Bringing a shop's records in, and taking them out again. §6.33 `MIG-001`, `MIG-005`.
 * Posts to `POST /migration/imports/validate`, `POST /migration/imports` and
 * `POST /migration/exports`.
 *
 * **This is the screen an optician needs on their first day and the product had none.** M3 puts a
 * real shop live before v1 is finished, and that shop arrives with a spreadsheet of customers and
 * a book of udhaar. `migration.controller.ts` has had five routes and no caller, so the only way
 * in was a developer with the tenant's database password.
 *
 * **The dry run is the screen, and the commit is a second decision.** `MIG-001` splits them and so
 * does this: validating writes nothing to the shop's data and answers with the whole report, so
 * what the file would do is read before it does it. A quarantined row is shown with its cells,
 * because "row 412 is wrong" sends somebody back to a spreadsheet with 5,000 rows in it.
 *
 * **Seven of the eight templates cannot be committed, and the screen says why in the server's own
 * words.** `NOT_COMMITTABLE_BECAUSE` holds a sentence per template naming the column that has
 * nowhere to land: a prescriptions row cannot say which eye the cylinder belongs to, a frames row
 * has a retail price and a frame record has no price, a lens prices row belongs to a price book
 * version and `PRC-010` publishes those frozen. Validation works for all eight, which is worth
 * having on its own: a shop can find out what is wrong with its data before anybody writes a line
 * of importer.
 *
 * **Two things it deliberately does not do.**
 *
 * - **No column mapping.** `ImportRequest` takes a `columnMap` and the server auto-matches
 *   otherwise, reporting every required column that found no header. The screen shows those
 *   problems and names the header to rename in the spreadsheet, rather than growing a mapping
 *   table, because the templates in `IMPORT_TEMPLATES` give the exact column names Appendix AA
 *   specifies and renaming a header is a thing a shopkeeper can do.
 * - **No resume.** `POST /migration/imports/:runId/resume` finishes a run that stopped, and takes
 *   the same file because "a run is identified by its bytes". Re-validating the same file reaches
 *   the same run, so the path is not lost, and a resume button would need the file still in hand
 *   after a reload.
 *
 * **The export is a download and not a report.** `MIG-005` makes it the shop's own data, audited,
 * and filtered by what the exporter may view, so the answer carries `withheld` beside the file:
 * what a reader without the cost row or the prescription row did not get is said rather than
 * silently missing.
 */

interface ValidateAnswer {
  readonly runId?: string
  readonly state?: string
  readonly rowCount?: number
  readonly action?: string
  readonly totals?: {
    readonly read: number
    readonly create: number
    readonly supersede: number
    readonly skipUnchanged: number
    readonly quarantined: number
    readonly warnings: number
  }
  readonly groups?: readonly IssueGroup[]
  readonly quarantined?: readonly QuarantineItem[]
  readonly mappingProblems?: readonly MappingProblem[]
}

interface ExportAnswer {
  readonly filename?: string
  readonly zipBase64?: string
  readonly withheld?: readonly { readonly entity: string; readonly reason: string }[]
  readonly notExported?: readonly { readonly entity: string; readonly reason: string }[]
}

export interface DataFilesProps {
  /** `BR-01`: imported rows belong to a branch, and the server checks the caller holds it. */
  readonly branchId: string
  /** The shop's own currency, as the file's default. The file may be in another. */
  readonly currency: string
  /** `catalogue_bulk_import`, Appendix B's one import row (owner and manager). */
  readonly canImport: boolean
  /** `customer_export`. A different row, so a shop can let somebody take data out and not put it in. */
  readonly canExport: boolean
  /**
   * Whether this reader also holds the row the template's own records need.
   *
   * `importPermissionsFor` asks for the import row **and** the entity's row: a customers file
   * writes customer records, so `customer_create_edit` governs it too. Appendix B's one import row
   * is labelled "Catalogue" and a customer is not catalogue, which the server records as a gap.
   */
  readonly canWriteCustomers: boolean
  /** Hands the finished zip to the browser. Injected, because a download is not a thing a test can do. */
  readonly offerDownload?: (filename: string, zipBase64: string) => void
}

/** Minor-unit digits, as Appendix AA leaves them to the operator. PKR is 2. */
const DIGITS = ['0', '2', '3'] as const

export function DataFiles({
  branchId,
  currency,
  canImport,
  canExport,
  canWriteCustomers,
  offerDownload,
}: DataFilesProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [template, setTemplate] = useState<ImportTemplateId>('customers')
  const [csv, setCsv] = useState('')
  const [fileCurrency, setFileCurrency] = useState(currency)
  const [minorDigits, setMinorDigits] = useState('2')
  const [validateId, setValidateId] = useState<string | null>(null)
  const [commitId, setCommitId] = useState<string | null>(null)
  const [exportId, setExportId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const validated = settledWrite(snapshot, validateId)
  const committed = settledWrite(snapshot, commitId)
  const exported = settledWrite(snapshot, exportId)
  const report = (validated?.response ?? undefined) as ValidateAnswer | undefined
  const exportAnswer = (exported?.response ?? undefined) as ExportAnswer | undefined

  // The sentence, or null for the one template that can be brought in. The table lives in
  // `status-labels.ts` because a key built at a call site is invisible to the i18n guard.
  const whyNotCommittable = importNotCommittableWord(t, template)
  const errors = (report?.groups ?? []).filter((group) => group.severity === 'error')
  const mappingProblems = report?.mappingProblems ?? []
  /**
   * Whether the file itself is fit to commit: it has been read, the columns matched, and no row
   * was set aside. The server refuses each of those, and a refused write holds the head of the
   * queue (`OFF-001a`).
   *
   * **The other three conditions are not here, and a mutation pass is how that got decided.** The
   * import row, the entity's row and the template all have their own branch in the render chain
   * below, in the order a person can fix them in, and each of those branches returns before this
   * one is reached. Repeating them here read as defence in depth and was unreachable code: weaken
   * either condition and every case stayed green, because the branch above had already won. The
   * chain is the lock, and `data-files.test.tsx` drives all three of its arms.
   */
  const fileIsCommittable =
    report !== undefined && mappingProblems.length === 0 && errors.length === 0

  const body = useCallback(
    () => ({
      template,
      csv,
      currency: fileCurrency.trim().toUpperCase(),
      minorDigits: Number(minorDigits),
      branchId,
    }),
    [branchId, csv, fileCurrency, minorDigits, template],
  )

  const checkFirst = useCallback((): boolean => {
    if (csv.trim() === '') {
      setError(t('dataFiles.fileRequired'))
      return false
    }
    if (!/^[A-Z]{3}$/.test(fileCurrency.trim().toUpperCase())) {
      setError(t('dataFiles.currencyInvalid'))
      return false
    }
    setError(null)
    return true
  }, [csv, fileCurrency, t])

  const validate = useCallback(async () => {
    if (!checkFirst()) return
    const write = (await enqueue({
      method: 'POST',
      path: '/migration/imports/validate',
      label: t('dataFiles.queueValidateLabel'),
      body: body(),
    })) as { id: string }
    setCommitId(null)
    setValidateId(write.id)
  }, [body, checkFirst, enqueue, t])

  const commit = useCallback(async () => {
    if (!checkFirst()) return
    const write = (await enqueue({
      method: 'POST',
      path: '/migration/imports',
      label: t('dataFiles.queueImportLabel'),
      // The same body as the dry run, deliberately: the server identifies a run by the file's
      // bytes, so a commit of a different file is a different run rather than a confirmation of
      // the one that was just read.
      body: body(),
    })) as { id: string }
    setCommitId(write.id)
  }, [body, checkFirst, enqueue, t])

  const runExport = useCallback(async () => {
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/migration/exports',
      label: t('dataFiles.queueExportLabel'),
      // No `entities`: absent means everything this build can export, which is what a shop asking
      // for its data means. Choosing a subset is a filter nobody has asked for.
      body: {},
    })) as { id: string }
    setExportId(write.id)
  }, [enqueue, t])

  const chosen = IMPORT_TEMPLATES.find((candidate) => candidate.id === template)
  const validateLine = statusLine(t, snapshot, validateId, t('queue.subject.dryRun'))
  const commitLine = statusLine(t, snapshot, commitId, t('queue.subject.dataImport'))
  const exportLine = statusLine(t, snapshot, exportId, t('queue.subject.dataExport'))

  return (
    <section className="lm-screen" aria-labelledby="data-files-heading">
      <h1 id="data-files-heading" className="lm-screen__heading">
        {t('dataFiles.heading')}
      </h1>

      {canImport ? (
        <>
          <h2 className="lm-screen__subheading">{t('dataFiles.importHeading')}</h2>
          <p className="lm-hint">{t('dataFiles.dryRunMeans')}</p>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('dataFiles.templateLabel')}</span>
            <select
              className="lm-input"
              value={template}
              onChange={(e) => {
                setTemplate(e.currentTarget.value as ImportTemplateId)
                // The report belongs to the file and the template together, so it is not kept
                // across a change of template: a report read against the wrong template is worse
                // than no report.
                setValidateId(null)
                setCommitId(null)
              }}
            >
              {IMPORT_TEMPLATES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {chosen === undefined ? null : (
            <p className="lm-hint">
              {t('dataFiles.columnsAre', {
                required: chosen.required.join(', '),
              })}
            </p>
          )}

          <label className="lm-field">
            <span className="lm-field__label">{t('dataFiles.fileLabel')}</span>
            <textarea
              className="lm-input lm-input--area"
              rows={6}
              value={csv}
              onChange={(e) => {
                setCsv(e.currentTarget.value)
                setValidateId(null)
                setCommitId(null)
              }}
            />
          </label>
          <p className="lm-hint">{t('dataFiles.fileMeans')}</p>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('dataFiles.currencyLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              autoComplete="off"
              value={fileCurrency}
              onChange={(e) => {
                setFileCurrency(e.currentTarget.value.toUpperCase())
              }}
            />
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('dataFiles.digitsLabel')}</span>
            <select
              className="lm-input"
              value={minorDigits}
              onChange={(e) => {
                setMinorDigits(e.currentTarget.value)
              }}
            >
              {DIGITS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <p className="lm-hint">{t('dataFiles.digitsMeans')}</p>

          <button
            type="button"
            className="lm-button lm-button--secondary"
            disabled={validateId !== null && validated === undefined}
            onClick={() => {
              void validate()
            }}
          >
            {t('dataFiles.validate')}
          </button>
          {validateLine === null ? null : (
            <p className="lm-queue-state" role="status">
              {validateLine}
            </p>
          )}

          {report === undefined ? null : (
            <div className="lm-import-report">
              <h3>{t('dataFiles.reportHeading')}</h3>
              {report.totals === undefined ? (
                <p className="lm-hint">{t('dataFiles.noPlan')}</p>
              ) : (
                <ul className="lm-import-totals">
                  <li>
                    {t('dataFiles.totalRead')} <Ltr>{String(report.totals.read)}</Ltr>
                  </li>
                  <li>
                    {t('dataFiles.totalCreate')} <Ltr>{String(report.totals.create)}</Ltr>
                  </li>
                  <li>
                    {t('dataFiles.totalSupersede')} <Ltr>{String(report.totals.supersede)}</Ltr>
                  </li>
                  <li>
                    {t('dataFiles.totalUnchanged')} <Ltr>{String(report.totals.skipUnchanged)}</Ltr>
                  </li>
                  <li>
                    {t('dataFiles.totalQuarantined')} <Ltr>{String(report.totals.quarantined)}</Ltr>
                  </li>
                  <li>
                    {t('dataFiles.totalWarnings')} <Ltr>{String(report.totals.warnings)}</Ltr>
                  </li>
                </ul>
              )}

              {mappingProblems.length === 0 ? null : (
                <ul className="lm-import-problems">
                  {mappingProblems.map((problem, index) => (
                    <li key={`${problem.code}-${String(index)}`}>
                      {/* The column and the header both, because the fix is renaming one to the
                          other in the spreadsheet and naming only one leaves the person guessing. */}
                      {t('dataFiles.mappingProblem', {
                        code: problem.code,
                        column: problem.column ?? '',
                        header: problem.header ?? '',
                      })}
                    </li>
                  ))}
                </ul>
              )}

              {(report.groups ?? []).length === 0 ? (
                <p className="lm-hint">{t('dataFiles.nothingWrong')}</p>
              ) : (
                <ul className="lm-import-groups">
                  {(report.groups ?? []).map((group) => (
                    <li key={`${group.code}-${group.column ?? ''}`}>
                      <span className={group.severity === 'error' ? 'lm-badge' : 'lm-chip'}>
                        {group.severity === 'error'
                          ? t('dataFiles.severityError')
                          : t('dataFiles.severityWarning')}
                      </span>{' '}
                      {t('dataFiles.issueGroup', {
                        code: group.code,
                        column: group.column ?? '',
                        count: group.count,
                      })}{' '}
                      <Ltr>{group.rows.slice(0, 10).join(', ')}</Ltr>
                    </li>
                  ))}
                </ul>
              )}

              {(report.quarantined ?? []).length === 0 ? null : (
                <ul className="lm-import-quarantine">
                  {(report.quarantined ?? []).map((item) => (
                    <li key={item.rowNumber}>
                      <span className="lm-quarantine__row">
                        {t('dataFiles.quarantineRow', { row: item.rowNumber })}
                      </span>
                      {/* The cells, because "row 412 is wrong" sends somebody back to a
                          spreadsheet with five thousand rows in it. */}
                      <span className="lm-quarantine__cells">
                        {Object.entries(item.cells)
                          .map(([key, value]) => `${key}: ${value}`)
                          .join(' | ')}
                      </span>
                      <span className="lm-quarantine__why">
                        {item.issues.map((issue) => issue.message).join(' ')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {whyNotCommittable !== null ? (
            <p className="lm-hint" role="status">
              {t('dataFiles.validatesOnly')} {whyNotCommittable}
            </p>
          ) : !canWriteCustomers ? (
            <p className="lm-hint" role="status">
              {t('dataFiles.needsCustomerRow')}
            </p>
          ) : fileIsCommittable ? (
            <>
              <p className="lm-hint">{t('dataFiles.commitMeans')}</p>
              <button
                type="button"
                className="lm-button lm-button--primary"
                disabled={commitId !== null && committed === undefined}
                onClick={() => {
                  void commit()
                }}
              >
                {t('dataFiles.commit')}
              </button>
            </>
          ) : report === undefined ? null : (
            <p className="lm-hint" role="status">
              {t('dataFiles.fixFirst')}
            </p>
          )}

          {commitLine === null ? null : (
            <p className="lm-queue-state" role="status">
              {commitLine}
            </p>
          )}
          {committed === undefined ? null : (
            <p className="lm-hint" role="status">
              {t('dataFiles.imported')}
            </p>
          )}
        </>
      ) : (
        <p className="lm-hint">{t('dataFiles.importNeedsOwner')}</p>
      )}

      {canExport ? (
        <>
          <h2 className="lm-screen__subheading">{t('dataFiles.exportHeading')}</h2>
          <p className="lm-hint">{t('dataFiles.exportMeans')}</p>
          <button
            type="button"
            className="lm-button lm-button--secondary"
            disabled={exportId !== null && exported === undefined}
            onClick={() => {
              void runExport()
            }}
          >
            {t('dataFiles.exportNow')}
          </button>
          {exportLine === null ? null : (
            <p className="lm-queue-state" role="status">
              {exportLine}
            </p>
          )}
          {exportAnswer === undefined ? null : (
            <div className="lm-export-answer">
              {exportAnswer.filename === undefined || exportAnswer.zipBase64 === undefined ? (
                <p className="lm-hint">{t('dataFiles.exportNoFile')}</p>
              ) : (
                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  onClick={() => {
                    offerDownload?.(
                      exportAnswer.filename as string,
                      exportAnswer.zipBase64 as string,
                    )
                  }}
                >
                  {t('dataFiles.download', { filename: exportAnswer.filename })}
                </button>
              )}
              {(exportAnswer.withheld ?? []).length === 0 ? null : (
                <ul className="lm-export-withheld">
                  {(exportAnswer.withheld ?? []).map((entry) => (
                    <li key={entry.entity}>
                      {/* `ROLE-001a`: what this reader did not get, said rather than missing. */}
                      {t('dataFiles.withheld', { entity: entry.entity, reason: entry.reason })}
                    </li>
                  ))}
                </ul>
              )}
              {(exportAnswer.notExported ?? []).length === 0 ? null : (
                <ul className="lm-export-withheld">
                  {(exportAnswer.notExported ?? []).map((entry) => (
                    <li key={entry.entity}>
                      {t('dataFiles.notExported', { entity: entry.entity, reason: entry.reason })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      ) : null}

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}
